import React, { useEffect } from "react";
import { Badge, Box, Grid, Paper, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useOpenRecallCounts, useVesselInfo } from "../../fetch/fetchVesselInfo";
import useModelInfoRows from "./useModelInfoRows";
import EngineInfoModelTab from "./EngineInfoModelTab";
import EngineInfoMaintenanceTab from "./EngineInfoMaintenanceTab";
import EngineInfoHistoryTab from "./EngineInfoHistoryTab";
import Spinner from "../spinner/Spinner";
import { parameterHelper } from "../../helpers";
import { useSelector } from "react-redux";
import { logFeatureUsage, logVesselInfoView } from "../../fetch/insightsHelper";
import { usePhoneMode } from "../../hooks";

export default function EngineInfoPage() {
    const { isMobile, isLandscape } = usePhoneMode();
    const location = useLocation();
    const navigate = useNavigate();
    const hasVesselHistory = useSelector((state) => state.permissions.hasVesselHistory);
    const { tab } = parameterHelper.getUrlParameters({ tab: 'history' });
    const theme = useTheme();
    const scrollViewerTop = theme.appBar.height;
    const { serialNumber } = useParams();
    let value = 0;
    switch (tab) {
        case 'model':
            value = 1;
            break;
        case 'maintenance':
            value = 2;
            break;
        case 'history':
        default:
            value = hasVesselHistory ? 0 : 1;
            break;
    }

    useEffect(() => {
        if (!!serialNumber) {
            logFeatureUsage('EngineInfoPage');
            logVesselInfoView(serialNumber);
        }
    }, [serialNumber]);

    const { data, isLoading } = useVesselInfo(serialNumber);
    const { data: openRecallCounts } = useOpenRecallCounts(serialNumber);
    const { basicInfo, maintenanceInfo } = data || {};
    const { events } = basicInfo || {};

    const modelInfoRows = useModelInfoRows(basicInfo, isLoading);

    const handleChange = (event, newValue) => {
        const params = new URLSearchParams(location.search);

        let tab;
        switch (newValue) {
            case 2:
                tab = 'maintenance';
                break;
            case 1:
                tab = 'model';
                break;
            case 0:
            default:
                tab = hasVesselHistory ? null : 'model';
                break;
        }

        if (!tab) {
            params.delete('tab');
        } else {
            params.set('tab', tab);
        }

        navigate(`${location.pathname}?${params.toString()}`);
    };

    return (
        <Grid
            id='scroll-viewer'
            container
            style={{
                position: 'fixed',
                top: scrollViewerTop,
                left: 0,
                height: `calc(100vh - ${scrollViewerTop}px)`,
                overflowX: 'hidden',
                overflowY: 'hidden',
                width: '100vw',
                padding: '1.5em 1em 1.5em 1em'
            }}
        >
            {isLoading ? (<Grid item xs={12}>
                <div style={{ position: 'relative', left: 'calc(50% - 30px)' }}>
                    <Spinner />
                </div>
            </Grid>) : (
                <Grid container columns={{ xs: 1 }} spacing={2}>
                    <Grid item xs={1}>
                        <Paper
                            sx={{
                                pt: 1.5,
                                pb: 1.5,
                                pr: !isMobile || isLandscape ? 1.5 : 0,
                                pl: !isMobile || isLandscape ? 1.5 : 0,
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                position: 'relative'
                            }}
                        >
                            <Tabs value={value} onChange={handleChange}>
                                <Tab label="History" style={{ display: hasVesselHistory ? null : 'none', padding: isMobile? '6px 8px' : null }} iconPosition="end" icon={openRecallCounts?.openBulletinCount > 0 ? (
                                    <Badge
                                        badgeContent={openRecallCounts?.openBulletinCount}
                                        color="error"
                                        style={{ marginLeft: '1em' }}
                                        aria-label={`${openRecallCounts?.openBulletinCount} Open Recalls`}
                                    />
                                ) : null} {...a11yProps(0)} />
                                <Tab label="Model Info" {...a11yProps(1)} style={{ padding: isMobile? '6px 8px' : null }} />
                                <Tab label="Maintenance" {...a11yProps(2)} style={{ display: !!maintenanceInfo ? null : 'none', padding: isMobile? '6px 8px' : null }} />
                            </Tabs>
                            <VesselInfoTabPanel value={value} index={1}>
                                <EngineInfoModelTab rows={modelInfoRows} />
                            </VesselInfoTabPanel>
                            <VesselInfoTabPanel value={value} index={2}>
                                {!!maintenanceInfo ? <EngineInfoMaintenanceTab maintenanceInfo={maintenanceInfo} /> : null}
                            </VesselInfoTabPanel>
                            <VesselInfoTabPanel value={value} index={0}>
                                <EngineInfoHistoryTab rows={events} />
                            </VesselInfoTabPanel>
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Grid>
    )
}

function VesselInfoTabPanel({ children, value, index }) {
    const { isMobile } = usePhoneMode();
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vi-tabpanel-${index}`}
            aria-labelledby={`vi-tab-${index}`}
        >
            {value === index && <Box sx={{ p: isMobile ? 0 : 1.5 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `vi-tab-${index}`,
        'aria-controls': `vi-tabpanel-${index}`,
    };
}